'use client'

import LifecycleLink from '@/components/common/LifecycleLink'
import clsx from 'clsx'
import { FiArrowUpRight } from 'react-icons/fi'
import { ChatLiveIcon } from '@/components/svg/enhanced'
import useI18n from 'Hooks/useI18n'
import useTipsyStudioAccess from '@/hooks/useTipsyStudioAccess'
import { withLocale } from '@/locales'
import { FANTACY_STUDIO_PATH } from '@/constant/fantacyStudio'

interface FantacyStudioEntryCardProps {
  compact?: boolean
  className?: string
  variant?: 'card' | 'inline'
}

export default function FantacyStudioEntryCard({
  compact = false,
  className,
  variant = 'card',
}: FantacyStudioEntryCardProps) {
  const { t, lang } = useI18n()
  const { isStudioAllowed } = useTipsyStudioAccess()
  const href = withLocale(FANTACY_STUDIO_PATH, lang)

  if (!isStudioAllowed) {
    return null
  }

  if (variant === 'inline') {
    return (
      <LifecycleLink
        href={href}
        prefetch
        className={clsx(
          'pointer-events-auto inline-flex h-9 items-center gap-2 rounded-full border border-secondary-border bg-secondary-bg px-4 py-1 text-sm text-white transition hover:border-hover-border',
          className
        )}
      >
        <span className="flex h-[18px] w-[18px] shrink-0 items-center justify-center overflow-hidden">
          <ChatLiveIcon
            size={28}
            color="#F8F1F4"
            showBackground={false}
            className="shrink-0"
          />
        </span>
        <span className="truncate font-medium">
          <span>{/* i18n-ignore */}Studio</span>
        </span>
      </LifecycleLink>
    )
  }

  return (
    <LifecycleLink
      href={href}
      prefetch
      className={clsx(
        'group relative block overflow-hidden rounded-[28px] border border-white/10',
        'bg-[linear-gradient(135deg,rgba(28,20,37,0.96),rgba(49,33,36,0.94)_58%,rgba(88,47,67,0.92))]',
        'shadow-[0_24px_80px_rgba(0,0,0,0.28)] transition duration-300 hover:-translate-y-0.5 hover:border-white/20',
        compact ? 'min-h-[152px] p-4' : 'min-h-[188px] p-5 md:p-6',
        className
      )}
    >
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -right-8 top-0 h-28 w-28 rounded-full bg-[#FF8A00]/20 blur-3xl transition duration-300 group-hover:scale-110" />
        <div className="absolute bottom-0 left-8 h-24 w-24 rounded-full bg-[#9D7BFF]/20 blur-3xl transition duration-300 group-hover:scale-110" />
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(255,255,255,0.12),transparent_34%),linear-gradient(180deg,transparent,rgba(255,255,255,0.03))]" />
      </div>

      <div className="relative flex h-full flex-col justify-between gap-5">
        <div className="flex items-start justify-between gap-4">
          <div className="flex min-w-0 items-center gap-3">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl border border-white/15 bg-black/20 backdrop-blur-xl">
              <ChatLiveIcon size={28} color="#F8F1F4" showBackground={false} />
            </div>
            <div className="min-w-0">
              <div className="inline-flex rounded-full border border-white/10 bg-white/[0.08] px-2.5 py-1 text-[11px] uppercase tracking-[0.24em] text-white/60">
                {t('New')}
              </div>
              <div
                className={clsx(
                  'mt-2 truncate font-semibold tracking-[0.02em] text-white',
                  compact ? 'text-xl' : 'text-[26px]'
                )}
              >
                <span>{/* i18n-ignore */}Tipsy Studio</span>
              </div>
            </div>
          </div>

          <div className="rounded-full border border-white/10 bg-white/[0.08] p-2 text-white/70 transition duration-300 group-hover:-translate-y-0.5 group-hover:translate-x-0.5 group-hover:text-white">
            <FiArrowUpRight className="h-4 w-4" />
          </div>
        </div>

        <div className="flex items-end justify-between gap-3">
          <div className="flex min-w-0 items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[#FF8A00] shadow-[0_0_16px_rgba(255,138,0,0.7)]" />
            <span className="truncate text-sm text-white/[0.55]">
              {/* i18n-ignore */}
              studio.infra.fantacy.live
            </span>
          </div>

          <div className="inline-flex shrink-0 items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-[#1B1320] transition duration-300 group-hover:bg-[#FFF2E3]">
            <span>{t('Open')}</span>
            <FiArrowUpRight className="h-4 w-4" />
          </div>
        </div>
      </div>
    </LifecycleLink>
  )
}
